import { useCallback } from "react";
import { useDispatch, useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchInboxItems,
  fetchInboxUnreadCount,
  inboxListFetchArgs,
  markInboxItemReviewed,
  selectInboxCoveredLetters,
  selectNextInboxDocumentId,
} from "../slice/InboxSlice";
import {
  CANCEL,
  KEEP,
  useInboxReviewConfirm,
} from "../context/InboxReviewConfirmContext";

/**
 * Mark one or more inbox letters reviewed, then drop the viewer onto
 * the next letter down — Outlook waterfall, not wrap-to-top.
 *
 * The next-document lookup MUST run against the list as it stands
 * before the rows leave. After they are gone the picker used to treat
 * the current letter as missing and land on whatever was sitting at
 * the top of the page.
 *
 * Inbox delegation (Matthew, 2026-10-08): when any of the leaving
 * letters is in this inbox because the reader covers someone else's
 * mail, ask first — "Remove from Chris Stratton's mailbox?". Remove
 * marks it reviewed for both; Keep marks it for the reader only
 * (`propagate: false`); Cancel does nothing and resolves
 * `{ cancelled: true }`. The reader's own letters never prompt.
 */
const useMarkInboxReviewed = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const store = useStore();
  const confirm = useInboxReviewConfirm();

  return useCallback(
    async (leavingIds) => {
      const ids = [
        ...new Set(
          (Array.isArray(leavingIds) ? leavingIds : [leavingIds]).filter(
            (id) => id != null && id !== ""
          )
        ),
      ];
      if (!ids.length) return undefined;

      let propagate;
      const covered = selectInboxCoveredLetters(store.getState(), ids);
      if (covered.count > 0 && confirm?.confirmRemoveFromMailbox) {
        const answer = await confirm.confirmRemoveFromMailbox(covered);
        if (answer === CANCEL) return { cancelled: true };
        propagate = answer !== KEEP;
      }

      const nextId = selectNextInboxDocumentId(store.getState(), ids);
      await Promise.all(
        ids.map((documentId) =>
          dispatch(markInboxItemReviewed({ documentId, propagate })).unwrap()
        )
      );
      dispatch(fetchInboxUnreadCount());
      navigate(nextId ? `/inbox/${nextId}` : "/inbox");
      dispatch(fetchInboxItems(inboxListFetchArgs(store.getState())));
      return undefined;
    },
    [confirm, dispatch, navigate, store]
  );
};

export default useMarkInboxReviewed;
